/**
 * El portal del cliente. **Mobile-first de verdad**: el médico entra desde el
 * celular, entre paciente y paciente, no desde un escritorio.
 *
 * Cinco destinos en la barra inferior y ninguno más. Si algo no cabe ahí, va en
 * «Más».
 *
 * En escritorio (≥901px) el marco se abre a 1280px y las pantallas se parten
 * en dos columnas; lo que es solo del portal vive en `portal.css`.
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/session';
import { Brandmark } from '@/components/common/Brandmark';
import { Icon, type IconName } from '@/components/common/NavIcons';
import '@/styles/portal.css';

/* Íconos por nombre, no glifos: «★» y «✦» se volvían emoji en el teléfono. */
const TABS: { to: string; label: string; icon: IconName; end?: boolean }[] = [
  { to: '/portal', label: 'Inicio', icon: 'inicio', end: true },
  { to: '/portal/cobros', label: 'Cobros', icon: 'cobros' },
  { to: '/portal/servicios', label: 'Servicios', icon: 'servicios' },
  { to: '/portal/mejora', label: 'Mejora', icon: 'mejora' },
  /* «Mi cuenta» y no «Más»: la pantalla dejó de ser un cajón de sastre y pasó
     a ser la cuenta de la persona con sus tres solapas. Y «Más» no le decía a
     nadie que ahí adentro estaban la contraseña y los avisos del teléfono
     (dueño, 8-set-2026). */
  { to: '/portal/mas', label: 'Mi cuenta', icon: 'perfil' },
];

/* El título de la pantalla sale de la ruta: en escritorio la cabecera lo
   muestra como `<h1>`; en el teléfono queda solo para el lector de pantalla
   (ver `.mhead__title` en portal.css), porque ahí la pestaña activa ya lo
   dice. `perfil` no es pestaña pero sí pantalla del portal. */
const TITULOS: [prefijo: string, titulo: string][] = [
  ['/portal/cobros', 'Cobros'],
  ['/portal/servicios', 'Servicios'],
  ['/portal/mejora', 'Mejora'],
  ['/portal/mas', 'Mi cuenta'],
];

export function PortalLayout() {
  const { user } = useSession();
  const { pathname } = useLocation();
  const titulo = TITULOS.find(([prefijo]) => pathname.startsWith(prefijo))?.[1] ?? 'Inicio';

  return (
    <div className="mobile-shell">
      <header className="mhead">
        <div className="mhead__top">
          <Brandmark size={30} />
          <span>
            <span className="mhead__name">Clean Blue</span>
            <span className="mhead__sub" style={{ display: 'block' }}>{user?.full_name}</span>
          </span>
        </div>
        <h1 className="mhead__title">{titulo}</h1>
      </header>

      <div className="mobile-shell__body">
        <Outlet />
      </div>

      <nav className="bottomnav">
        {/* Solo se ve en el rail de escritorio (`.railbrand` en app.css): en
            el teléfono la marca sigue viviendo en `.mhead__top`, arriba. */}
        <div className="railbrand">
          <div className="railbrand__logo">
            <Brandmark size={27} />
            <span className="railbrand__name">Clean Blue</span>
          </div>
          <span className="railbrand__org">{user?.full_name}</span>
        </div>
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => (isActive ? 'is-active' : '')}
          >
            <span className="ico" aria-hidden="true"><Icon name={tab.icon} size={20} /></span>
            {tab.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
